/**
 * Cross-feature chrome strings. Every other user-facing string lives in the
 * owning feature's `copy.ts` — there is no top-level `src/copy/` bag in any
 * frontend repo.
 *
 * The vocabulary below is the pack's, checked against the design system's own
 * HTML twin first and against Arabic streaming convention second. Where the
 * twin has already named something, it wins: `تابع المشاهدة` rather than
 * Netflix Arabic's `متابعة المشاهدة`, and `سجّل الدخول` in the imperative
 * rather than the commoner nominal `تسجيل الدخول`.
 */
export const chromeCopy = {
  skipToContent: 'تخطَّ إلى المحتوى',
  brand: 'شاهدني',
  brandHomeLabel: 'شاهدني — الصفحة الرئيسية',
  /**
   * The pre-session lockup points at the PUBLIC host: this area's own home
   * needs a session, and a reader who has not got one would be sent back to
   * the wall they just came through.
   */
  brandPublicSiteLabel: 'شاهدني — الموقع العامّ',
  primaryNavLabel: 'التنقّل الرئيسي',
  /**
   * The bottom bar is a SECOND navigation landmark on the same page, and two
   * landmarks sharing a name are indistinguishable in a screen reader's
   * landmark list — the reader is offered «التنقّل الرئيسي» twice and cannot
   * tell which one they are about to enter. `landmark-unique` is a
   * best-practice axe rule and was not enabled in the audit that missed this.
   */
  bottomNavLabel: 'التنقّل السفلي',
  accountMenuLabel: 'قائمة الحساب',
  breadcrumbLabel: 'مسار التنقّل',
  footerLabel: 'روابط الموقع',
  /** The subscription's single sentence, carried on every gated surface. */
  footerNote: 'اشتراك واحد يفتح الفهرس كاملاً — لا شراء منفصل لأي عنوان. تظهر التعليقات بعد مراجعتها.',
  /**
   * The rights line at the foot of every surface, worded exactly as the public
   * site words it — the two footers are one row of the same brand and a reader
   * crossing between the hosts must not meet two different sentences.
   */
  copyright: 'كلّ الحقوق محفوظة',

  nav: {
    home: 'الرئيسية',
    categories: 'التصنيفات',
    search: 'البحث',
    editorial: 'المقالات',
  },

  account: {
    /** Not `ملفّي` — that reads as «my file» and is not interface vocabulary. */
    profile: 'الملف الشخصي',
    lists: 'قائمتي',
    subscription: 'اشتراكي',
    /** Not `مراسلاتي` — the surface is one case queue across five types. */
    support: 'الدعم',
    settings: 'الإعدادات',
    signOut: 'تسجيل الخروج',
    /**
     * The confirmation NAMES ITS EFFECT rather than asking «هل أنت متأكّد؟»,
     * and the body states what does NOT change — the account, the list, and
     * the subscription all survive the sign-out, so the reader is answering a
     * question about this device rather than about their membership.
     */
    signOutConfirm: {
      title: 'تسجيل الخروج من هذا الجهاز',
      body: 'ينتهي الاشتراك في هذه الجلسة فقط. حسابك وقائمتك واشتراكك تبقى كما هي، وتعود بتسجيل الدخول مرّة أخرى.',
      confirm: 'سجّل الخروج',
      cancel: 'ابقَ في الحساب',
    },
    /** The avatar summary names whose account the menu belongs to. */
    menuFor: (displayName: string) => `حساب ${displayName}`,
    menuForAnonymous: 'حسابي',
  },

  topbar: {
    search: 'ابحث في الفهرس',
    /**
     * Names ONLY what the engine indexes. `backend-search` builds its
     * `search_document` from `title_normalized` and `description_normalized`
     * and from nothing else — there is no credit, person or cast column in the
     * index, and `[x]` §7.7 asks for «title and description» and no more. A
     * placeholder promising «ممثّل» sent readers looking for an actor by name
     * to a result set that could never contain one, and the results page's own
     * field label already says «ابحث في العناوين والأوصاف».
     */
    searchPlaceholder: 'ابحث عن فيلم أو مسلسل…',
    /** The magnifier changes ROLE, not state — two labels, one control. */
    openSearch: 'افتح حقل البحث',
    submitSearch: 'ابحث',
    toThemeLight: 'التبديل إلى الوضع الفاتح',
    toThemeDark: 'التبديل إلى الوضع الداكن',
  },

  notifications: {
    label: 'الرسائل',
    /** Colour alone may not carry meaning, so the dot has a sentence beside it. */
    unread: 'الرسائل — لديك رسائل غير مقروءة',
  },

  genreTree: {
    label: 'شجرة التصنيفات',
    /**
     * The row's destination is the FIRST ROOT TERM's own listing — there is no
     * bare `/genres` route on either host, because the tree below is the
     * vocabulary's surface. So the label names that term rather than promising
     * a page of all of them: «كلّ التصنيفات» over a link to «أكشن» is a label
     * the destination disproves on arrival.
     */
    all: (term: string) => `تصفّح ${term}`,
  },

  footer: {
    about: 'من نحن',
    contact: 'اتصل بنا',
    search: 'البحث',
    editorial: 'المقالات',
  },
} as const;
